import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Gamepad2, ArrowLeft, Loader } from 'lucide-react';
import api from '../api';
import GameForge from '../components/GameForge';

const ChallengePage = () => {
  const { shareCode } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [challengeData, setChallengeData] = useState(null);
  
  useEffect(() => {
    if (shareCode) {
      loadChallenge();
    }
  }, [shareCode]);
  
  const loadChallenge = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/games/challenge/${shareCode.toUpperCase()}`);
      setChallengeData(response.data);
    } catch (err) {
      console.error('Failed to load challenge:', err);
      setError(err.response?.data?.detail || 'Challenge not found. Check your code and try again!');
    } finally {
      setLoading(false);
    }
  };
  
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-900 via-purple-900/20 to-slate-900 flex items-center justify-center">
        <div className="text-center">
          <Loader className="w-12 h-12 text-purple-400 animate-spin mx-auto mb-4" />
          <p className="text-white text-lg">Loading Challenge...</p>
          <p className="text-slate-400 text-sm mt-2">Code: {shareCode?.toUpperCase()}</p>
        </div>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-900 via-purple-900/20 to-slate-900 flex items-center justify-center p-4">
        <div className="text-center max-w-md">
          <div className="text-6xl mb-4">😢</div>
          <h1 className="text-2xl font-bold text-white mb-2">Oops!</h1>
          <p className="text-slate-400 mb-6">{error}</p>
          <button
            onClick={() => navigate('/')}
            className="px-6 py-3 rounded-xl bg-purple-500 text-white font-semibold hover:bg-purple-600 transition-colors flex items-center gap-2 mx-auto"
          >
            <ArrowLeft className="w-4 h-4" />
            Go Home
          </button>
        </div>
      </div>
    );
  }
  
  if (challengeData) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-900 via-purple-900/20 to-slate-900">
        {/* Challenge Header */}
        <div className="bg-gradient-to-r from-purple-500/20 to-cyan-500/20 border-b border-purple-500/30 py-4 px-6">
          <div className="max-w-4xl mx-auto flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/')}
                className="p-2 rounded-lg bg-slate-800/50 text-slate-400 hover:text-white transition-colors"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div>
                <div className="flex items-center gap-2">
                  <Gamepad2 className="w-5 h-5 text-purple-400" />
                  <span className="text-purple-300 text-sm font-medium">Challenge</span>
                </div>
                <h1 className="text-white font-bold text-lg">
                  "{challengeData.name}" by {challengeData.creator_name}
                </h1>
              </div>
            </div>
            <div className="text-right">
              <p className="text-slate-400 text-xs">Challenge Code</p>
              <p className="text-purple-300 font-mono font-bold">{shareCode?.toUpperCase()}</p>
            </div>
          </div>
        </div>
        
        {/* Game Forge with pre-loaded challenge */}
        <div className="max-w-6xl mx-auto px-4 py-6">
          <GameForge 
            selectedChild={{ id: 'guest', name: 'Guest Player' }}
            onBack={() => navigate('/')}
            initialChallenge={challengeData}
          />
        </div>
      </div>
    );
  }
  
  return null;
};

export default ChallengePage;
